import { routesMap } from "@/components/routes/routes";
import axios from "@/services/middleware/client";
import type {
  SignupFormPayload,
  ForgotPasswordFormPayload,
  ResetPasswordFormPayload,
  PreRegisterPayload,
  SignupTrialFormPayload,
  LoginFormPayload,
  AppSumoSignupFormPayload,
} from "@/types/model/Auth";
import storageService from "@/services/middleware/storageService";
import { useAuthStore } from "@/zustand/authStore";
import { useLoaderStore } from "@/zustand/loaderStore";
import { useNotificationStore } from "@/zustand/notificationsStore";
import { pushEvent } from "../gtm/gtmService";
import { PLAN_MODE } from "@/utilities/constants";

export function doLogin(params: LoginFormPayload) {
  axios
    .post("/auth/login", params)
    .then((res) => {
      useAuthStore.getState().setLogin(res.data);
      useLoaderStore.getState().setLoading(false);
    })
    .catch((error) => {
      useLoaderStore.getState().setLoading(false);
      if (error.response?.status === 401) {
        useNotificationStore.getState().error("E-Mail or password not valid");
      }

      if (error.response?.status === 403) {
        window.location.href = routesMap.forbidden.path;
      }
    });
}

export function doPreRegister(params: PreRegisterPayload) {
  useLoaderStore.getState().setLoading(true);
  axios
    .post("/auth/preregister", params)
    .then((res) => {
      if (res.data.loginToken?.hash) {
        storageService.setItem("token", res.data.loginToken.hash);
        setTimeout(() => {
          useLoaderStore.getState().setLoading(false);
          window.location.href = routesMap.role.path;
        }, 1000);
      }
    })
    .catch((error) => {
      useLoaderStore.getState().setLoading(false);
      if (error.response?.status === 401) {
        useNotificationStore
          .getState()
          .error("Your token is invalid or has expired");
      }
    });
}

export function doSignup(params: SignupFormPayload) {
  axios
    .post("/auth/signup", params)
    .then(() => {
      useAuthStore.getState().setSignup();
    })
    .catch((error) => {
      if (error.response?.status === 403) {
        window.location.href = `/verify-your-email?email=${params.email}`;
      }
    });
}

export function doTrialSignup(params: SignupTrialFormPayload) {
  useLoaderStore.getState().setLoading(true);
  axios
    .post("/auth/trial-signup", params)
    .then((res) => {
      // Autologin
      storageService.setItem("token", res.data.loginToken.hash);
      storageService.removeItem("referrer");
    })
    .then(() => {
      pushEvent("opticks_trigger", {
        value: 0.0,
        currency: "EUR",
        plan_name: PLAN_MODE.FREE,
        trigger_type: "signup_free",
        transaction_id: Date.now().toString(),
        items: [
          {
            item_name: "signup_free",
            item_variant: PLAN_MODE.FREE,
          },
        ],
      });
    })
    .then(() => {
      useLoaderStore.getState().setLoading(false);
      window.location.href = params.idToken
        ? routesMap.role.path
        : routesMap.auth.trialEmailVerificationPending.path;
    })
    .catch(() => {
      useLoaderStore.getState().setLoading(false);
    });
}

export function doPlanSignup(params: SignupTrialFormPayload) {
  axios
    .post("/auth/validate-signup", params)
    .then(() => {
      storageService.removeItem("referrer");
      useAuthStore.getState().setSignupPlanUserValidation();
    })
    .catch(() => {
      useLoaderStore.getState().setLoading(false);
    });
}

export function doForgotPassword(params: ForgotPasswordFormPayload) {
  axios.post("/auth/password/forgot", params).catch((error) => {
    console.error("axios error", error); // eslint-disable-line no-console
  });
}

export function doResetPassword(params: ResetPasswordFormPayload) {
  axios
    .post("/auth/password/reset", params)
    .then((res) => {
      useAuthStore.getState().setResetPassword(res.data);
    })
    .catch((error) => {
      console.error("axios error", error); // eslint-disable-line no-console
    });
}

export function doResendVerificationEmail(params: ForgotPasswordFormPayload) {
  axios.post("/auth/email/resend-confirmation", params).catch((error) => {
    if (error.response?.status === 404) {
      useNotificationStore.getState().error("E-Mail not found");
    }
  });
}

export function doAppSumoSignup(params: AppSumoSignupFormPayload) {
  useLoaderStore.getState().setLoading(true);
  axios
    .post("/auth/appsumo-signup", params)
    .then((res) => {
      if (res.data.loginToken?.hash) {
        storageService.setItem("token", res.data.loginToken.hash);
        setTimeout(() => {
          useLoaderStore.getState().setLoading(false);
          window.location.href = routesMap.base.path;
        }, 1000);
      }
    })
    .catch((error) => {
      useLoaderStore.getState().setLoading(false);
      if (error.response?.data?.message) {
        useNotificationStore.getState().error(error.response.data.message);
      } else {
        useNotificationStore
          .getState()
          .error("An error occurred during signup. Please try again.");
      }
    });
}
